const extractOrganizationCode = (url) => {
  return /^(.*)\.com/g.exec(url)[1]
}

export class FreshdeskController {
  constructor (client) {
    this.client = client
    this.system = 'freshdesk'
    this.onUpdate = () => {}
    this.onAddNote = () => {}
  }

  fetchCredentials () {
    return new Promise((resolve, reject) => {
      this.client.db.get('unbabelToken').then(resolve, reject)
    })
  }

  uploadAuthorization (organizationId, token) {
    return new Promise((resolve, reject) => {
      this.client.db
        .set('unbabelToken', { organizationId, token })
        .then(resolve, (err) => {
          if (err.status === 404) {
            resolve({ organizationId: null, token: null })
          } else {
            reject(err)
          }
        })
    })
  }

  clearAuthorization () {
    return this.uploadAuthorization('', '')
  }

  async validateToken (accessToken) {
    return this.fetchAgents(accessToken)
  }

  /**
   * Should always return a list of objects in the form:
   * [{ code: '123', name: 'John Doe', email: 'john@doe.com', language: 'en' }, ...]
   */
  async fetchAgents (accessToken = null) {
    const organizationCode = await this.fetchOrganizationCode()
    const url = `https://${organizationCode}.com/api/v2/agents`
    const encode = (username, password = 'X') =>
      window.btoa(unescape(encodeURIComponent(`${username}:${password}`)))

    const options = accessToken ? {
      headers: {'Authorization': `Basic ${encode(accessToken)}`}
    } : {}

    return this._paginate(url, options, ({ id, contact: { name, email, language } }) => {
      return { code: id, name, email, language }
    })
  }

  _paginate (url, options, mappingFunction) {
    return new Promise((resolve, reject) => {
      this.client.request.get(url, options)
        .then((r) => {
          const { response, headers } = r
          const pageResult = JSON.parse(response)
            .map(mappingFunction || response)

          let nextPage = (headers || {}).link
          if (nextPage) {
            nextPage = nextPage.match('<(.*)>')[1]
            this._paginate(nextPage, options, mappingFunction)
              .then((result) => {
                resolve(pageResult + result)
              })
          } else {
            resolve(pageResult)
          }
        }, (err) => {
          reject(err)
        })
    })
  }

  fetchClientAgent () {
    return this.client.data.get('loggedInUser')
      .then(({ loggedInUser: { id, agent, contact: { language, email } } }) => {
        if (id) {
          return { agentCode: id, agentEmail: email, preferredLanguage: language }
        } else {
          return { agentCode: agent.id, agentEmail: email, preferredLanguage: language }
        }
      })
  }

  getTicketDetails (agentCode = null) {
    const agent = agentCode || this.fetchClientAgent().then(({ agentCode }) => agentCode)
    const ticket = this.client.data.get('ticket')
    return Promise.all([ticket, agent])
      .then(([{ ticket: { id: threadCode, status, subject, attachments, requester_id: authorCode, ...rest } }, agentCode]) => {
        let content = ''
        let contentHtml = ''
        if (rest.description_html) {
          content = rest.description
          contentHtml = rest.description_html
        } else {
          content = rest.description_text
          contentHtml = rest.description
        }

        return {
          status,
          subject,
          content,
          agentCode,
          threadCode,
          authorCode,
          attachments,
          contentHtml
        }
      })
  }

  getThreadForMessagePayload (agentCode = null) {
    return this.getTicketDetails(agentCode)
      .then(({ subject, content, authorRole = 'customer', authorCode, threadCode, contentHtml, agentCode }) => {
        const messageCode = `${threadCode}_description`
        return {
          subject,
          content,
          authorRole: 'customer',
          authorCode,
          threadCode,
          messageCode,
          contentHtml,
          threadData: {triggered_by: agentCode},
          messageData: {triggered_by: agentCode}
        }
      })
  }

  resizeApplet ({ height }) {
    return this.client.instance.resize({ height: `${height}px` })
  }

  closeApplet () {
    return this.resizeApplet({ height: 0 })
  }

  reloadParent () {}

  prependTextToNote (text) {
    this.client.interface.trigger('setValue', {id: 'editor', text})
  }

  fetchOrganizationCode () {
    return new Promise((resolve, reject) => {
      this.client.data.get('domainName')
        .then(({ domainName }) => resolve(extractOrganizationCode(domainName)), reject)
    })
  }

  _fetchDomainName () {
    return new Promise((resolve, reject) => {
      this.client.data.get('domainName')
        .then(({ domainName }) => resolve({ domainName }), reject)
    })
  }

  async fetchOnboardingParams () {
    const { agentCode } = await this.fetchClientAgent()
    const { threadCode } = await this.getTicketDetails()
    const { domainName } = await this._fetchDomainName()
    const organizationCode = await this.fetchOrganizationCode()

    const debugParam = process.env.NODE_ENV === 'development' ? '?dev=true' : ''
    const referrer = `https://${domainName}/a/tickets/${threadCode}${debugParam}`

    const options = {
      'referer_url': referrer,
      'agent_code': agentCode,
      'agent_role': 'admin',
      'organization_code': organizationCode
    }
    const encoded = Object.keys(options)
      .map((key) => `${key}=${encodeURIComponent(options[key])}`).join('&')

    const url = `${window.MESSAGES_ENDPOINT}${window.ONBOARDING_URL}?${encoded}`
    return url
  }

  async fetchPortalSettingsUrl () {
    const installationId = await this.fetchOrganizationCode() + '.com'

    return `${window.MESSAGES_ENDPOINT}/api/frontdesk/v1/settings/${installationId}`
  }

  async fetchOnboardingPortalParams () {
    const { agentCode, agentEmail } = await this.fetchClientAgent()
    const { threadCode } = await this.getTicketDetails()
    const { domainName } = await this._fetchDomainName()
    const installationId = await this.fetchOrganizationCode() + '.com'

    const referrer = `https://${domainName}/a/tickets/${threadCode}`

    const options = {
      'final_redirect_uri': referrer,
      'admin_external_id': agentCode,
      'agent_role': 'admin',
      'admin_email': agentEmail,
      'installation_id': installationId
    }
    const encoded = Object.keys(options)
      .map((key) => `${key}=${encodeURIComponent(options[key])}`).join('&')

    return `${window.MESSAGES_ENDPOINT}/api/frontdesk/v1/onboarding?${encoded}`
  }

  static getSystemName () {
    return this.system
  }

  static initialize () {
    const app = window.FSH_APP

    if (!app) {
      throw Error('No freshdesk app object present')
    }

    return app.initialized()
      .then((client) => {
        const controller = new FreshdeskController(client)
        client.events.on('ticket.notesClick', (event) => {
          controller.onAddNote(event)
        })
        client.events.on('ticket.addNote', (event) => {
          controller.onUpdate(event)
        })
        return controller
      })
  }

  generateFetch () {
    // return this.methodInvocationFetch()
    return this._generateFetch()
  }

  methodInvocationFetch () {
    return (url, options) => {
      return new Promise((resolve, reject) => {
        options.url = url
        this.client.request.invoke('serverMethod', options)
          .then(({ response: { body, status, url, method, err } }) => {
            if (err) {
              return reject(err)
            }
            resolve(new Response(body, { status }))
          }, reject)
      })
    }
  }

  _generateFetch () {
    const { client } = this
    return (url, options) => {
      return new Promise((resolve, reject) => {
        var method = options.method || 'get'
        options.method = null
        const resolver = (promise) => {
          promise.then(
            (data) => {
              resolve(new Response(data.response, data))
            },
            (err) => reject(err)
          )
        }

        switch (method.toLocaleLowerCase()) {
          case 'get':
            resolver(client.request.get(url, options))
            break
          case 'post':
            resolver(client.request.post(url, options))
            break
          case 'put':
            resolver(client.request.put(url, options))
            break
          case 'patch':
            resolver(client.request.patch(url, options))
            break
        }
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/api/freshdesk.js